import React, { useEffect, useMemo, useState } from "react";
import { Plus, FlaskConical, Camera, Trash2, Edit, Search } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/layout/PageHeader";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";

const EMPTY = {
  code: "", name: "", category: "lab", subcategory: "", specimen: "", modality: "",
  body_site: "", loinc: "", prep_instructions: "", active: true,
};

const toForm = (t) => ({
  code: t.code || "", name: t.name || "", category: t.category || "lab", subcategory: t.subcategory || "",
  specimen: t.specimen || "", modality: t.modality || "", body_site: t.body_site || "",
  loinc: t.loinc || "", prep_instructions: t.prep_instructions || "", active: t.active !== false,
});

export default function TestCatalog() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [sheet, setSheet] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState({ open: false });

  async function load() {
    setLoading(true);
    try { const r = await api.get("/test-catalog"); setItems(r.data.items || []); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((t) => {
      if (catFilter !== "all" && t.category !== catFilter) return false;
      if (!q) return true;
      return `${t.name} ${t.code} ${t.subcategory || ""}`.toLowerCase().includes(q);
    });
  }, [items, search, catFilter]);

  function openNew() { setEditing(null); setForm(EMPTY); setSheet(true); }
  function openEdit(t) { setEditing(t); setForm(toForm(t)); setSheet(true); }

  async function save() {
    if (!form.code.trim() || !form.name.trim()) { toast.error("Code and name are required"); return; }
    setSaving(true);
    const payload = {
      code: form.code.trim().toUpperCase(), name: form.name.trim(), category: form.category,
      subcategory: form.subcategory.trim() || null, specimen: form.specimen.trim() || null,
      modality: form.modality.trim() || null, body_site: form.body_site.trim() || null,
      loinc: form.loinc.trim() || null, prep_instructions: form.prep_instructions.trim() || null,
      active: form.active,
    };
    try {
      if (editing) await api.put(`/test-catalog/${editing.id}`, payload);
      else await api.post("/test-catalog", payload);
      toast.success(editing ? "Test updated" : "Test added");
      setSheet(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  }

  async function doDelete(t) {
    try { await api.delete(`/test-catalog/${t.id}`); toast.success("Test removed"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setConfirm({ open: false }); }
  }

  const isImg = (c) => c === "imaging";

  return (
    <div data-testid="test-catalog-page">
      <PageHeader
        title="Lab & Imaging Catalog"
        subtitle="Structured lab and imaging test catalog used for ordering — codes, specimens, modalities and prep instructions."
        actions={
          <Button onClick={openNew} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="test-new-btn">
            <Plus className="w-4 h-4 mr-1.5" /> New Test
          </Button>
        }
      />

      <div className="hah-card p-3 mb-3 flex items-center gap-2">
        <Search className="w-4 h-4 text-[#94A3B8]" />
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search tests…"
               data-testid="test-search" className="flex-1 bg-transparent text-sm outline-none" />
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger className="h-8 w-36" data-testid="test-cat-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            <SelectItem value="lab">Lab</SelectItem>
            <SelectItem value="imaging">Imaging</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-xs text-[#94A3B8]">{filtered.length} test(s)</span>
      </div>

      {loading ? (
        <div className="hah-card p-8 text-center text-sm text-[#6B7280]">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="hah-card p-10 text-center text-sm text-[#6B7280]" data-testid="test-empty">No tests found.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {filtered.map((t) => {
            const Icon = isImg(t.category) ? Camera : FlaskConical;
            const color = isImg(t.category) ? "#F59E0B" : "#0EA5E9";
            return (
              <div key={t.id} data-testid={`test-card-${t.code}`} className="hah-card p-4 flex flex-col gap-2 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-[15px] font-semibold text-[#1F2937] truncate flex items-center gap-1.5">
                      <Icon className="w-4 h-4 shrink-0" style={{ color }} /> {t.name}
                    </div>
                    <div className="text-[11px] text-[#94A3B8] font-mono">{t.code}{t.subcategory ? ` · ${t.subcategory}` : ""}</div>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium capitalize" style={{ background: `${color}1A`, color }}>{t.category}</span>
                    {!t.active && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#F1F5F9] text-[#64748B] font-medium">Inactive</span>}
                  </div>
                </div>
                <div className="text-[12px] text-[#6B7280]">
                  {isImg(t.category) ? [t.modality, t.body_site].filter(Boolean).join(" · ") : [t.specimen, t.loinc && `LOINC ${t.loinc}`].filter(Boolean).join(" · ")}
                </div>
                {t.prep_instructions && <div className="text-[11px] text-[#B45309] bg-[#FEF3C7] rounded px-2 py-1">Prep: {t.prep_instructions}</div>}
                <div className="flex items-center gap-2 pt-2 border-t border-[#F1F5F9] mt-auto">
                  <button onClick={() => openEdit(t)} data-testid={`test-edit-${t.code}`} className="text-[12px] text-[#0B7E8C] hover:underline inline-flex items-center gap-1"><Edit className="w-3.5 h-3.5" /> Edit</button>
                  <button onClick={() => setConfirm({ open: true, item: t })} data-testid={`test-delete-${t.code}`} className="text-[12px] text-[#EF4444] hover:underline inline-flex items-center gap-1 ml-auto"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Sheet open={sheet} onOpenChange={setSheet}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="test-sheet">
          <SheetHeader>
            <SheetTitle>{editing ? "Edit Test" : "New Test"}</SheetTitle>
            <SheetDescription>Define a lab or imaging test for structured ordering.</SheetDescription>
          </SheetHeader>
          <div className="space-y-4 mt-5">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Code *"><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="CBC" data-testid="test-form-code" /></Field>
              <Field label="Category">
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="test-form-category"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="lab">Lab</SelectItem><SelectItem value="imaging">Imaging</SelectItem></SelectContent>
                </Select>
              </Field>
            </div>
            <Field label="Name *"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Complete Blood Count" data-testid="test-form-name" /></Field>
            <Field label="Subcategory"><Input value={form.subcategory} onChange={(e) => setForm({ ...form, subcategory: e.target.value })} placeholder="Hematology / Radiography" /></Field>
            {form.category === "lab" ? (
              <div className="grid grid-cols-2 gap-3">
                <Field label="Specimen"><Input value={form.specimen} onChange={(e) => setForm({ ...form, specimen: e.target.value })} placeholder="Whole blood" /></Field>
                <Field label="LOINC"><Input value={form.loinc} onChange={(e) => setForm({ ...form, loinc: e.target.value })} placeholder="58410-2" /></Field>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <Field label="Modality"><Input value={form.modality} onChange={(e) => setForm({ ...form, modality: e.target.value })} placeholder="X-ray / CT / US / MRI" /></Field>
                <Field label="Body Site"><Input value={form.body_site} onChange={(e) => setForm({ ...form, body_site: e.target.value })} placeholder="Chest" /></Field>
              </div>
            )}
            <Field label="Prep Instructions"><Input value={form.prep_instructions} onChange={(e) => setForm({ ...form, prep_instructions: e.target.value })} placeholder="Fasting 8 hours" /></Field>
            <Field label="Active">
              <Select value={form.active ? "yes" : "no"} onValueChange={(v) => setForm({ ...form, active: v === "yes" })}>
                <SelectTrigger data-testid="test-form-active"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="yes">Active</SelectItem><SelectItem value="no">Inactive</SelectItem></SelectContent>
              </Select>
            </Field>
          </div>
          <div className="flex items-center gap-2 mt-6 pt-4 border-t border-[#E5E7EB]">
            <Button onClick={save} disabled={saving} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="test-save-btn">
              {saving ? "Saving…" : editing ? "Save changes" : "Add test"}
            </Button>
            <Button variant="outline" onClick={() => setSheet(false)} className="border-[#E5E7EB]">Cancel</Button>
          </div>
        </SheetContent>
      </Sheet>

      <ConfirmDialog
        open={confirm.open}
        onOpenChange={(o) => setConfirm({ ...confirm, open: o })}
        title="Delete test?"
        description={`"${confirm.item?.name}" will be permanently removed from the catalog.`}
        confirmLabel="Delete" destructive
        onConfirm={() => doDelete(confirm.item)}
      />
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[12px] font-medium text-[#374151] mb-1">{label}</label>
      {children}
    </div>
  );
}
